import { UpdateButton, useUpdate } from 'react-admin';
import { useGetAppConfig } from './useGetAppConfig';
import { useOnSettled } from './useOnSettled';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  Switch,
  Alert,
  Divider,
} from '@mui/material';
import { Semaphore } from './Semaphore';
import { Block, Dangerous, Edit, Warning } from '@mui/icons-material';

export const BlockerContentUpdateForm = () => {
  const [updateConfig, { isLoading: isUpdatingBlockerContent }] =
    useUpdate('therapistAppConfig', { id: 'current' });
  const [
    updateBlockerEnabled,
    { isLoading: isUpdatingBlockerEnabled },
  ] = useUpdate('therapistAppConfig', { id: 'current' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] =
    useState(false);
  const [confirmationStep, setConfirmationStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [messageConfirmed, setMessageConfirmed] = useState(false);

  const { remoteAppConfig } = useGetAppConfig();
  const { globalBlocker_content, globalBlocker_enabled } =
    remoteAppConfig || {};

  const { register, handleSubmit, formState, setValue, reset } =
    useForm({
      defaultValues: {
        globalBlocker_content,
      },
    });

  useEffect(() => {
    setValue('globalBlocker_content', globalBlocker_content);
  }, [globalBlocker_content, setValue]);

  const { onSettled } = useOnSettled();

  const doSubmit = useCallback(
    async (data: any) => {
      await updateConfig(
        'therapistAppConfig',
        {
          id: 'current',
          data,
        },
        {
          onSettled,
        },
      );

      reset(data);
      setDialogOpen(false);
    },
    [updateConfig, onSettled, reset],
  );

  const onSubmit = handleSubmit(doSubmit);

  const handleOpenDialog = useCallback(() => {
    setValue('globalBlocker_content', globalBlocker_content);
    setDialogOpen(true);
  }, [globalBlocker_content, setValue]);

  const handleCloseDialog = useCallback(() => {
    setValue('globalBlocker_content', globalBlocker_content);
    reset({ globalBlocker_content });
    setDialogOpen(false);
  }, [globalBlocker_content, setValue, reset]);

  const handleToggleBlocker = useCallback(() => {
    setConfirmationStep(1);
    setConfirmText('');
    setMessageConfirmed(false);
    setConfirmationDialogOpen(true);
  }, []);

  const handleCloseConfirmation = useCallback(() => {
    setConfirmationDialogOpen(false);
    setConfirmationStep(1);
    setConfirmText('');
    setMessageConfirmed(false);
  }, []);

  const handleConfirmStep1 = useCallback(() => {
    if (confirmText === 'ENABLE GLOBAL BLOCKER') {
      setConfirmationStep(2);
    }
  }, [confirmText]);

  const handleFinalConfirm = useCallback(async () => {
    if (!messageConfirmed) {
      return;
    }

    await updateBlockerEnabled(
      'therapistAppConfig',
      {
        id: 'current',
        data: {
          globalBlocker_enabled: !globalBlocker_enabled,
        },
      },
      {
        onSettled,
      },
    );

    handleCloseConfirmation();
  }, [
    messageConfirmed,
    updateBlockerEnabled,
    globalBlocker_enabled,
    onSettled,
    handleCloseConfirmation,
  ]);

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        marginBottom={2}
      >
        <Semaphore enabled={globalBlocker_enabled} />
        <Typography variant="body2" color="text.secondary">
          {globalBlocker_enabled ? 'Enabled' : 'Disabled'}
        </Typography>
      </Box>

      <Button
        variant="contained"
        color={!globalBlocker_enabled ? 'error' : 'success'}
        startIcon={globalBlocker_enabled ? <Block /> : <Dangerous />}
        onClick={handleToggleBlocker}
        disabled={isUpdatingBlockerEnabled}
        sx={{ color: '#FFF' }}
      >
        {globalBlocker_enabled
          ? 'Disable Global Blocker'
          : 'ENABLE GLOBAL BLOCKER'}
      </Button>

      <Box marginTop="1rem">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          marginBottom={1}
        >
          <Typography variant="subtitle2" color="text.secondary">
            Blocker Content
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit />}
            onClick={handleOpenDialog}
          >
            Edit
          </Button>
        </Box>
        <Paper
          variant="outlined"
          sx={{
            padding: '1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxWidth: '600px',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              color: 'text.primary',
            }}
          >
            {globalBlocker_content || '(No content)'}
          </Typography>
        </Paper>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={onSubmit}>
          <DialogTitle>Edit Blocker Content</DialogTitle>
          <DialogContent>
            <TextField
              multiline
              fullWidth
              rows={10}
              label="Blocker Content"
              disabled={isUpdatingBlockerContent}
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              {...register('globalBlocker_content', {
                required: true,
              })}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              disabled={isUpdatingBlockerContent}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                isUpdatingBlockerContent || !formState.isValid
              }
              startIcon={
                isUpdatingBlockerContent && (
                  <CircularProgress size={16} />
                )
              }
            >
              {isUpdatingBlockerContent ? 'Updating' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Multi-step Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={handleCloseConfirmation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmationStep === 1
            ? '⚠️ First Confirmation Required'
            : '⚠️ Final Confirmation Required'}
        </DialogTitle>
        <DialogContent>
          {confirmationStep === 1 ? (
            <Box>
              <Alert
                severity="error"
                icon={<Warning />}
                sx={{ marginBottom: 2 }}
              >
                <Typography variant="body2" fontWeight="bold">
                  This action will{' '}
                  {globalBlocker_enabled
                    ? 'allow all users to resume regular usage'
                    : 'prevent all users from using the app normally'}
                  .
                </Typography>
              </Alert>
              <Typography variant="body2" marginBottom={2}>
                To proceed, please type{' '}
                <strong>"ENABLE GLOBAL BLOCKER"</strong> in the field
                below:
              </Typography>
              <TextField
                fullWidth
                label="Type 'ENABLE GLOBAL BLOCKER' to continue"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                autoFocus
                error={
                  confirmText.length > 0 &&
                  confirmText !== 'ENABLE GLOBAL BLOCKER'
                }
                helperText={
                  confirmText.length > 0 &&
                  confirmText !== 'ENABLE GLOBAL BLOCKER'
                    ? "Must type exactly 'ENABLE GLOBAL BLOCKER'"
                    : ''
                }
              />
            </Box>
          ) : (
            <Box>
              <Alert
                severity="error"
                icon={<Warning />}
                sx={{ marginBottom: 2 }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Final confirmation required. Please review the
                  blocker message that will be shown to users.
                </Typography>
              </Alert>

              <Typography variant="subtitle2" marginBottom={1}>
                Blocker Message Preview:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  padding: '1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  marginBottom: 2,
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'text.primary',
                  }}
                >
                  {globalBlocker_content ||
                    '(No content - users will see empty message)'}
                </Typography>
              </Paper>

              <Divider sx={{ marginY: 2 }} />

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding={1}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2">
                  I confirm this is the message I want to show to all
                  users
                </Typography>
                <Switch
                  checked={messageConfirmed}
                  onChange={e =>
                    setMessageConfirmed(e.target.checked)
                  }
                  color="error"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseConfirmation}
            disabled={isUpdatingBlockerEnabled}
          >
            Cancel
          </Button>
          {confirmationStep === 1 ? (
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmStep1}
              disabled={confirmText !== 'ENABLE GLOBAL BLOCKER'}
            >
              Continue to Final Confirmation
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              onClick={handleFinalConfirm}
              disabled={!messageConfirmed || isUpdatingBlockerEnabled}
              startIcon={
                isUpdatingBlockerEnabled && (
                  <CircularProgress size={16} />
                )
              }
            >
              {isUpdatingBlockerEnabled
                ? 'Processing...'
                : 'ENABLE GLOBAL BLOCKER'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

type ToggleButtonProps = {
  id?: string;
  resource: string;
  enabled: boolean;
  enableText?: string;
  disableText?: string;
  confirmTitle: string;
  confirmContent: string;
  data: any;
};

export const ToggleButton = ({
  id,
  resource,
  enabled,
  confirmTitle,
  enableText = ' Enable',
  disableText = 'Disable',
  confirmContent,
  data,
}: ToggleButtonProps) => {
  const { onSettled } = useOnSettled();

  return (
    <UpdateButton
      id={id}
      resource={resource}
      variant="contained"
      color={!enabled ? 'success' : 'error'}
      label={enabled ? disableText : enableText}
      icon={enabled ? <Block /> : <Dangerous />}
      confirmTitle={confirmTitle}
      confirmContent={confirmContent}
      mutationMode="pessimistic"
      sx={{ color: '#FFF' }}
      mutationOptions={{
        meta: { id },
        onSettled,
      }}
      data={data}
    />
  );
};
